import {
  type DOMCompatibleAttributes,
  ExtensionTag,
  NodeExtension,
  type NodeExtensionSpec,
} from "remirror";
import type { CommandFunction, ProsemirrorNode } from "remirror";

import { VideoPlayer } from "simplydo/react/components/VideoPlayer";

type VideoExtensionOptions = Record<string, unknown> & { srcRegex?: RegExp };

export class VideoExtension extends NodeExtension {
  name = "video";

  tags = [ExtensionTag.InlineNode];

  constructor(options: VideoExtensionOptions) {
    super({
      ...options,
      extraAttributes: {
        src: { default: null },
        controls: { default: "true" },
        allowfullscreen: { default: "true" },
      },
    });
  }

  ReactComponent = ({ node }: { node: ProsemirrorNode }) => {
    return <VideoPlayer {...node.attrs} />;
  };

  createNodeSpec(): NodeExtensionSpec {
    return {
      inline: true,
      attrs: {
        src: { default: "" },
        controls: { default: "true" },
        allowfullscreen: { default: "true" },
      },
      selectable: true,
      draggable: true,
      atom: true,
      content: "",
      toDOM: (node: ProsemirrorNode) => {
        const attrs: DOMCompatibleAttributes = {
          src: node.attrs.src,
          controls: node.attrs.controls,
          allowfullscreen: node.attrs.allowfullscreen,
        };
        return ["video", attrs];
      },
      parseDOM: [
        {
          tag: "video",
          node: "video",
          priority: 55,
          attrs: {
            src: { default: "" },
            controls: { default: "true" },
            allowfullscreen: { default: "true" },
          },
          getAttrs: (dom: Node) => {
            const node = dom as HTMLVideoElement;
            const src = node.getAttribute("src");
            const controls = node.getAttribute("controls") || "true";
            const allowfullscreen =
              node.getAttribute("allowfullscreen") || "true";

            return { src, controls, allowfullscreen };
          },
        },
        {
          tag: "span[data-node='video']",
          node: "video",
          priority: 60,
          attrs: {
            src: { default: "" },
            controls: { default: "true" },
            allowfullscreen: { default: "true" },
          },
          getAttrs: (dom: Node) => {
            const node = dom as HTMLSpanElement;
            const videoNode = node.children[0] as HTMLVideoElement;
            const src = videoNode.getAttribute("src");
            const controls = videoNode.getAttribute("controls") || "true";
            const allowfullscreen =
              videoNode.getAttribute("allowfullscreen") || "true";

            return { src, controls, allowfullscreen };
          },
        },
      ],
    };
  }

  insertCommand(param: CommandFunction, attrs: Record<string, unknown>) {
    const { tr, dispatch } = param;
    const { selection } = tr;
    const position =
      selection.$cursor ? selection.$cursor.pos : selection.$to.pos;

    // @ts-ignore
    const node = this.type.create(attrs);
    dispatch(tr.insert(position, node).insertText(" "));
    return true;
  }

  createCommands() {
    return {
      insertVideo:
        ({ src }: { src: string }) =>
        (param: CommandFunction) => {
          if (!src) {
            return false;
          }
          // @ts-ignore
          const { srcRegex } = this.options;
          if (srcRegex && !src.match(srcRegex)) {
            return false;
          }

          this.insertCommand(param, { src });
        },
    };
  }
}

export const createVideoExtension = (options: Array<Record<string, unknown>>) =>
  new VideoExtension({ ...(options[0] || {}), disableExtraAttributes: true });
